<script setup lang="ts">
import type { FormSubmitEvent } from '#ui/types';
import { phoneValidatorE164, SAVED_SEARCH_NAME_MAX_LENGTH } from '@lib/shared';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { z } from 'zod';

import { STORYBLOK_ROUTES } from '#shared/constants/storyblok';
import {
    apiCreateGuestSavedSearch,
    type BackendErrorEntity,
    type CreateGuestSavedSearchBodyDto,
    type CreateSavedSearchBodyDto,
    type ErrorType,
    getApiGetSavedSearchCustomersQueryKey,
    getApiGetSavedSearchesQueryKey,
    getApiGetTeamSavedSearchesQueryKey,
    type ListingFiltersDto,
    type SavedSearchCustomerEntity,
    useApiCreateSavedSearch,
    useApiCreateTeamSavedSearch,
    useApiGetSavedSearchCustomers,
    useApiUpdateAccountPersonalData,
    useApiUpdateSavedSearch,
} from '~/apiClient';
import type { PlatformSellingMethod } from '~/types/sale.type';

/** A guest's search is kept on the contacts they left, which their registration starts from (PT-750). */
type SaveSearchModalResult = Readonly<{ guestContacts: CustomerContacts | null }>;

type CustomerItem = SavedSearchCustomerEntity & { name: string; label: string };

// PROPS
interface Props {
    sellingMethod: PlatformSellingMethod;
    filters: ListingFiltersDto;
    suggestedName: string;
    editedSavedSearchId?: string;
}

const { sellingMethod, filters, suggestedName, editedSavedSearchId } = defineProps<Props>();

// MODEL
const open = defineModel<boolean>();

// EMITS
const emit = defineEmits<{
    close: [SaveSearchModalResult | undefined];
}>();

// CONSTANTS
const CUSTOMER_SEARCH_MIN_LENGTH = 2;

// COMPOSABLES
const { tt, locale } = useTypedI18n();
const { data: session, isLoggedIn, isFleequidTeam, getSession } = useAuthUtils();
const { isMobile, isLgOrGreater } = useResponsive();
const { notifySuccess, notifyError, notifyGenericError } = useNotification();
const { logError } = useLogs();
const { getCached, setCached } = useCustomerContactsCache();
const { turnstileContainerId, getToken, formatRequestOptions } = useInteractionOnlyTurnstile('saveSearch', { lazy: true });
const route = useRoute();
const queryClient = useQueryClient();
const { mutateAsync: mutateCreate, isPending: isCreating } = useApiCreateSavedSearch();
const { mutateAsync: mutateCreateAsGuest, isPending: isCreatingAsGuest } = useMutation<
    Awaited<ReturnType<typeof apiCreateGuestSavedSearch>>,
    ErrorType<BackendErrorEntity>,
    { data: CreateGuestSavedSearchBodyDto; token: string | null }
>({
    mutationFn: ({ data, token }) => apiCreateGuestSavedSearch(data, formatRequestOptions(token)),
});
const { mutateAsync: mutateCreateForCustomer, isPending: isCreatingForCustomer } = useApiCreateTeamSavedSearch();
const { mutateAsync: mutateUpdate, isPending: isUpdating } = useApiUpdateSavedSearch();
const { mutateAsync: mutatePersonalData, isPending: isUpdatingPersonalData } = useApiUpdateAccountPersonalData();

// DATA
const isEditing = editedSavedSearchId !== undefined;
const isGuest = !isEditing && !isLoggedIn.value;
// The Fleequid team may set the search up for a customer, who owns it and gets its emails (PT-748).
const canChooseCustomer = !isEditing && isFleequidTeam.value;
const cachedContacts = isGuest ? getCached() : null;
const hasPhone = Boolean(session.value?.user.phone);
const customer = ref<CustomerItem>();
const customerSearchTerm = ref('');
const customerSearchTermDebounced = refDebounced(customerSearchTerm, 300);
const customerSearchParams = computed(() => ({ q: customerSearchTermDebounced.value.trim() }));
const { data: customersData, isFetching: isSearchingCustomers } = useApiGetSavedSearchCustomers(customerSearchParams, {
    query: {
        enabled: computed(() => canChooseCustomer && customerSearchParams.value.q.length >= CUSTOMER_SEARCH_MIN_LENGTH),
        queryKey: getApiGetSavedSearchCustomersQueryKey(customerSearchParams),
    },
});
const customerItems = computed<CustomerItem[]>(() =>
    (customersData.value?.customers ?? []).map(item => {
        const name = formatPersonName(item) ?? item.email;

        return { ...item, name, label: [name, item.companyName, item.email].filter(Boolean).join(' · ') };
    })
);
// As for Buy Now, the sales rep receives the search as a lead, to call back: the phone is asked when there is none. A
// search the team sets up for a customer has no lead.
const needsPhone = computed(() => !isEditing && !customer.value && (isGuest || !hasPhone));
const formSchema = computed(() =>
    z.object({
        name: zodRequiredString(tt('validation.msg.required', { field: tt('savedSearches.modal.name') })).max(SAVED_SEARCH_NAME_MAX_LENGTH),
        email: isGuest ? z.email(tt('validation.msg.invalid', { field: tt('login.email') })) : z.string(),
        phone: needsPhone.value
            ? zodRequiredString(tt('validation.msg.required', { field: tt('leads.fields.phone') })).refine(phoneValidatorE164, {
                  message: tt('validation.msg.phone'),
              })
            : z.string(),
    })
);
type FormSchema = z.infer<typeof formSchema.value>;
const formData = reactive<FormSchema>({ name: suggestedName, email: cachedContacts?.email ?? '', phone: cachedContacts?.phone ?? '' });
const isSubmitting = computed(
    () => isCreating.value || isCreatingAsGuest.value || isCreatingForCustomer.value || isUpdating.value || isUpdatingPersonalData.value
);

// METHODS
const getSaveBody = (name: string): CreateSavedSearchBodyDto => ({
    name,
    sellingMethod,
    filters,
    language: localeToLanguage(locale.value),
    urlPath: route.fullPath,
});

const invalidateSavedSearches = (): Promise<unknown> =>
    Promise.all([
        queryClient.invalidateQueries({ queryKey: getApiGetSavedSearchesQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getApiGetTeamSavedSearchesQueryKey() }),
    ]);

const update = async (savedSearchId: string, { name }: FormSchema): Promise<void> => {
    await mutateUpdate({ savedSearchId, data: { name, sellingMethod, filters } });
    await invalidateSavedSearches();

    notifySuccess({ title: tt('savedSearches.notifications.updated.title'), description: name });
    emit('close', { guestContacts: null });
};

const saveForCustomer = async ({ userId, name: customerName }: CustomerItem, { name }: FormSchema): Promise<void> => {
    await mutateCreateForCustomer({ data: { ...getSaveBody(name), customerUserId: userId } });
    await invalidateSavedSearches();

    notifySuccess({
        title: tt('savedSearches.notifications.saved.title'),
        description: tt('savedSearches.notifications.saved.customerDescription', { customer: customerName }),
    });
    emit('close', { guestContacts: null });
};

const saveAsCustomer = async ({ name, phone }: FormSchema): Promise<void> => {
    if (needsPhone.value) {
        await mutatePersonalData({ data: { phone } });
        await getSession();
    }

    await mutateCreate({ data: getSaveBody(name) });
    await invalidateSavedSearches();

    notifySuccess({ title: tt('savedSearches.notifications.saved.title'), description: tt('savedSearches.notifications.saved.description') });
    emit('close', { guestContacts: null });
};

const saveAsGuest = async ({ name, email, phone }: FormSchema): Promise<void> => {
    let token: string | null;

    try {
        token = await getToken();
    } catch {
        notifyError({ title: tt('validation.msg.captcha'), description: tt('notifications.tryAgain') });

        return;
    }

    await mutateCreateAsGuest({ data: { ...getSaveBody(name), email, phone }, token });
    setCached({ email, phone });

    notifySuccess({
        title: tt('savedSearches.notifications.saved.title'),
        description: tt('savedSearches.notifications.saved.guestDescription', { email }),
    });
    emit('close', { guestContacts: { email, phone } });
};

// HANDLERS
const onSubmit = async ({ data }: FormSubmitEvent<FormSchema>): Promise<void> => {
    try {
        if (editedSavedSearchId !== undefined) await update(editedSavedSearchId, data);
        else if (customer.value) await saveForCustomer(customer.value, data);
        else await (isGuest ? saveAsGuest(data) : saveAsCustomer(data));
    } catch (error) {
        logError('Error saving the search', { filters, error });
        notifyGenericError();
    }
};
</script>

<template>
    <UModal
        v-model:open="open"
        :fullscreen="isMobile"
        :transition="!isMobile"
        :title="isEditing ? tt('savedSearches.modal.editTitle') : tt('savedSearches.modal.title')"
        v-bind="modalNoDescAriaFixupProps"
    >
        <template #body>
            <p class="mb-4 text-center">{{ tt('savedSearches.modal.description') }}</p>

            <UForm class="flex flex-col gap-4" :state="formData" :schema="formSchema" @submit="onSubmit">
                <UFormField :label="tt('savedSearches.modal.name')" name="name" required>
                    <UInput
                        v-model="formData.name"
                        class="w-full"
                        :maxlength="SAVED_SEARCH_NAME_MAX_LENGTH"
                        :disabled="isSubmitting"
                        :autofocus="isLgOrGreater"
                    />
                </UFormField>

                <UFormField v-if="canChooseCustomer" :label="tt('savedSearches.modal.customer')" :help="tt('savedSearches.modal.customerHelp')">
                    <UInputMenu
                        v-model="customer"
                        v-model:search-term="customerSearchTerm"
                        :items="customerItems"
                        :loading="isSearchingCustomers"
                        :placeholder="tt('savedSearches.modal.customerPlaceholder')"
                        :disabled="isSubmitting"
                        icon="lucide:user-search"
                        label-key="label"
                        autocomplete="off"
                        class="w-full"
                        clear
                        ignore-filter
                    />
                </UFormField>

                <UFormField v-if="isGuest" :label="tt('login.email')" name="email" required>
                    <UInput
                        v-model.trim="formData.email"
                        class="w-full"
                        autocomplete="email"
                        type="email"
                        inputmode="email"
                        :disabled="isSubmitting"
                    />
                </UFormField>

                <UFormField v-if="needsPhone" :label="tt('leads.fields.phone')" name="phone" required>
                    <template #hint>
                        <BasePhoneHint />
                    </template>
                    <BasePhoneInput v-model.trim="formData.phone" :disabled="isSubmitting" />
                </UFormField>

                <TurnstileChallenge v-if="isGuest" :container-id="turnstileContainerId" />

                <UButton
                    :label="isEditing ? tt('savedSearches.update') : tt('savedSearches.modal.submit')"
                    class="mt-4"
                    type="submit"
                    size="xl"
                    :loading="isSubmitting"
                    block
                />
            </UForm>

            <BaseI18nT v-if="isGuest" tag="p" class="mt-4 text-center" keypath="leads.privacy.base">
                <template #link>
                    <NuxtLinkLocale class="text-primary font-semibold underline" :to="STORYBLOK_ROUTES.PRIVACY_POLICY" target="_blank">
                        {{ tt('leads.privacy.link') }}
                    </NuxtLinkLocale>
                </template>
            </BaseI18nT>
        </template>
    </UModal>
</template>
